import { cn } from '@morpho-org/ui-v6/utils'
import { cva, type VariantProps } from 'class-variance-authority'
import { forwardRef, type HTMLAttributes, type ReactNode } from 'react'

/**
 * Tag variants using cva for Tailwind class management.
 *
 * Matches v5 Tag behavior:
 * - Border radius: 120px (pill shape)
 * - White-space: nowrap
 * - Width: max-content
 * - Flexbox: centered content with gap
 *
 * Variants:
 * - main: 18px height, 0-6px padding, 4xs text (10px), secondary color, primary bg
 * - secondary: 20px (h-md) height, 0-6px padding, 2xs text (12px), secondary color, primary bg
 * - bundler: 26px height, 0-8px padding, 3xs text (11px), secondary color, primary bg, cursor default
 * - pnl: 22px height, 0-6px padding, 3xs text (11px), interactive active color, disabled bg
 */
const tagVariants = cva(
  // Base styles matching v5 Tag
  [
    'rounded-[120px]',
    'whitespace-nowrap',
    'w-max',
    'flex',
    'items-center',
    'gap-[2px]',
    '!leading-none'
  ].join(' '),
  {
    variants: {
      variant: {
        main: [
          'px-[6px]',
          'py-0',
          'min-h-[18px]',
          'bg-muted',
          'text-4xs',
          'text-secondary-foreground'
        ].join(' '),
        secondary: [
          'px-[6px]',
          'py-2xs',
          'min-h-md',
          'bg-muted',
          'text-2xs',
          'text-secondary-foreground'
        ].join(' '),
        bundler: [
          'px-xs',
          'py-0',
          'min-h-[26px]',
          'bg-muted',
          'text-3xs',
          'text-secondary-foreground',
          'cursor-default'
        ].join(' '),
        pnl: [
          'px-[6px]',
          'py-0',
          'min-h-[22px]',
          'bg-primary/40',
          'text-3xs',
          'text-info-foreground'
        ].join(' ')
      }
    },
    defaultVariants: {
      variant: 'main'
    }
  }
)

export interface TagProps extends HTMLAttributes<HTMLDivElement>, VariantProps<typeof tagVariants> {
  /** Label text to display */
  label?: string
  /** Icon to display at the start */
  startIcon?: ReactNode
}

/**
 * Tag component - Tailwind replica of dapp-ui-v5 Tag
 *
 * Features:
 * - Pill-shaped background
 * - Four variants: main, secondary, bundler, pnl
 * - Support for label text and start icon
 * - Click handler support (cursor changes automatically when onClick is provided)
 *
 * @example
 * ```tsx
 * // Basic tag (10px text)
 * <Tag label="main" variant="main" />
 *
 * // Secondary variant (12px text, matches body-2xs)
 * <Tag label="secondary" variant="secondary" />
 *
 * // Bundler variant
 * <Tag label="bundler" variant="bundler" />
 *
 * // PnL variant
 * <Tag label="pnl" variant="pnl" />
 *
 * // With start icon
 * <Tag label="Tagged" startIcon={<Icon />} />
 *
 * // With custom styling
 * <Tag label="Custom" className="bg-blue-500" />
 * ```
 */
const Tag = forwardRef<HTMLDivElement, TagProps>(
  ({ className, variant, label, startIcon, children, onClick, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          tagVariants({ variant }),
          onClick && variant !== 'bundler' && 'cursor-pointer',
          className
        )}
        onClick={onClick}
        {...props}
      >
        {startIcon && <span className="flex items-center py-[2px]">{startIcon}</span>}
        {label && <span>{label}</span>}
        {children}
      </div>
    )
  }
)
Tag.displayName = 'Tag'

export { Tag, tagVariants }
